import { graphHooks } from '@tunasong/graph-lib/react'
import { Poll as PollIcon } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { ManyOfManyPollResponse } from '@tunasong/schemas'
import { isManyOfManyPoll, isManyOfManyPollResponse, isPersisted, isPoll } from '@tunasong/schemas'
import { EntityLinkById, Typography, Button, FormGroup, FormControl, FormControlLabel } from '@tunasong/ui-lib'
import type { FC } from 'react'
import React, { useMemo } from 'react'
import invariant from 'tiny-invariant'
import type { PollProps } from '../poll.js'
import styles from '../poll.module.css'
import { getProgress } from '../progress-util.js'
import Progress from '../progress.js'
import { sortPollOptionsIndices } from '../sort.js'
import VoteButton from '../vote-button.js'

export const ManyOfManyPollVote: FC<PollProps> = props => {
  const { poll, userId, onChange, sort } = props

  invariant(isManyOfManyPoll(poll), `Poll must be a ManyOfManyPoll`)

  const { userId: currentUserId } = useCurrentUser()
  const { profile: respondent } = graphHooks.useProfile(userId)
  const { createEntity, isCreatingEntity, createEntityResult } = graphHooks.useEntityGraph()
  const updateEntity = graphHooks.useEntityUpdate<ManyOfManyPollResponse>({ debounceDelay: 0 })

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } =
    graphHooks.useEntityChildren<ManyOfManyPollResponse>({
      parentId: poll.id,
      filter: 'pollresponse',
    })

  const queriedResponse = useMemo(
    () => allResponses.filter(response => response.userId === userId)[0],
    [allResponses, userId]
  )

  /**
   * This user's response, from whichever source knows about it first. The children query lags
   * the create by a refetch, so the queried one is undefined for a beat afterwards — long
   * enough for a second save to take the create path and write a duplicate.
   */
  const created = createEntityResult.data
  const savedResponse = queriedResponse ?? (isManyOfManyPollResponse(created) ? created : undefined)
  const finalResponse = savedResponse?.choices

  /**
   * The draft starts undefined rather than `{}` so a re-edit opens on the saved answer
   * instead of a blank slate. `{}` would have silently cleared every previous choice on
   * the next save.
   */
  const [draft, setDraft] = React.useState<Record<string, boolean> | undefined>(undefined)
  const currentResponses = draft ?? finalResponse ?? {}

  const isExternalRespondent = userId !== currentUserId

  invariant(isManyOfManyPoll(poll), 'Poll must be many of many')

  const handleChange = (idx: number) => (ev: unknown, val: boolean) => {
    invariant(hasLoadedChildren, 'Children should be loaded')
    setDraft({ ...currentResponses, [idx]: val })
  }

  const handleSave = async () => {
    if (!(userId && isPoll(poll) && currentResponses && isPersisted(poll))) {
      throw new Error(`userId must be set`)
    }

    /** One user, one response. Re-saving updates; it never writes a second pollresponse. */
    if (savedResponse) {
      updateEntity(savedResponse.id, { choices: currentResponses })
      return
    }

    const entity = {
      type: 'pollresponse',
      pollType: 'many-of-many',
      choices: currentResponses,
    } satisfies ManyOfManyPollResponse

    await createEntity({
      entity,
      parent: poll,
    })
  }

  const quota = poll.responseQuota ?? 0
  const isComplete =
    quota > 0
      ? currentResponses
        ? Object.values(currentResponses).reduce((prev, cur) => prev + (cur ? 1 : 0), 0) === quota
        : false
      : true

  const { name = 'Untitled', finalOption, closed } = poll

  const pollOptionsIndices = sort
    ? sortPollOptionsIndices(poll, allResponses, 'random')
    : poll.pollOptions.map((_, idx) => idx)

  /** If the responses object has an entry for us we're done */
  const isResponseSaved = Boolean(finalResponse)

  const { responseQuota = Number.MAX_SAFE_INTEGER } = poll
  const numUserResponses = currentResponses
    ? Object.values(currentResponses).reduce((acc, val) => acc + (val === true ? 1 : 0), 0)
    : 0
  const hasReachedQuota = numUserResponses >= responseQuota

  /**
   * Many-of-many previously had no change path at all: readOnly latched the moment a saved
   * response existed. Availability changes constantly, so an answer stays editable while the
   * poll says it may be. Without `allowChange` the old one-answer-final behaviour stands.
   */
  const canChange = Boolean(poll.allowChange) || !finalResponse

  /**
   * `hasCreatedEntity` used to sit in here as well, and it is a LATCH rather than an in-flight
   * signal: it stays true for the rest of the mount, so `Save my choices` went dead the instant
   * the first save landed and `allowChange` could not actually be exercised without a page
   * reload. `isCreatingEntity` is the real in-flight guard, and `savedResponse` above is what
   * keeps the post-create window from writing a second response.
   */
  const readOnly = Boolean(!Boolean(onChange) || poll.closed || !hasLoadedChildren || isCreatingEntity || !canChange)

  const responses = currentResponses

  return (
    <div className={styles.container}>
      <Typography variant="subtitle1" className={styles.title}>
        <PollIcon /> {name}
      </Typography>
      {poll.closed && (
        <Typography variant="caption" color="secondary">
          The Poll is closed.
        </Typography>
      )}
      {!isExternalRespondent && !poll.closed && !isResponseSaved && responseQuota < Number.MAX_SAFE_INTEGER && (
        <Typography variant="caption">
          You have given {numUserResponses} responses of {responseQuota} available.
        </Typography>
      )}
      {isResponseSaved ? (
        <Typography variant="caption" color="primary" display="block">
          {isExternalRespondent ? (respondent?.name ?? respondent?.email + ' has') : 'You have'} completed this poll.
        </Typography>
      ) : null}
      <FormControl className={styles.formControl}>
        <FormGroup>
          {hasLoadedChildren &&
            pollOptionsIndices &&
            pollOptionsIndices
              .map(idx => ({ option: poll.pollOptions[idx], idx }))
              .map(({ option, idx }) => (
                <FormControlLabel
                  key={idx}
                  value={idx}
                  control={
                    <VoteButton
                      disabled={readOnly || (hasReachedQuota && !responses?.[idx])}
                      status={responses?.[idx] ?? false}
                      onChange={handleChange(idx)}
                    />
                  }
                  label={
                    <div className={styles.optionLabel}>
                      {option.entityRef ? <EntityLinkById entityId={option.entityRef} /> : option.name}
                      {poll.closed && (
                        <Progress value={getProgress(poll, allResponses, idx)} selected={finalOption?.[idx]} />
                      )}
                    </div>
                  }
                  className={styles.formLabel}
                />
              ))}
        </FormGroup>
        {!isExternalRespondent && !closed ? (
          <Button disabled={!isComplete || readOnly} onClick={handleSave}>
            Save my choices
          </Button>
        ) : null}
      </FormControl>
    </div>
  )
}
